import React, { useEffect, useCallback, useMemo, useReducer } from 'react';
import axios from 'axios';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import FilterPanel from '@components/FilterPanel';
import SearchPanel from '@components/SearchPanel';
import I18n from '@utils/i18n';
import { KendoLocalizationProvider } from '@contexts/kendo-localization-context';
import api from '@utils/axios';
import useDebounce from '@hooks/useDebounce';
import { datatableAccountsPath } from 'routes';
import ColumnMenu from '@components/Kendo/ColumnMenu';
import ColumnMenuContext from '@components/Kendo/ColumnMenuContext';
import EnterKeyPager from '@components/Kendo/EnterKeyPager';
import kendoDatatableParams from '@helpers/kendoDatatableParams';
import { usePublishListContext } from '@features/AIChatWidget/hooks/usePublishListContext';
import { saveCacheToLocalStorage } from '../../../helpers/localStorage';
import { getInitialData } from './helpers/initialData';
import useFilters from './hooks/useFilters';
import './index.css';
import { actionTypes, reducer } from './reducer';
import { ALL_COLUMNS, ALL_COLUMNS_DATA, INITIAL_FILTERS, CACHE_KEY } from './constants';
import CustomScrollbar from '../../../components/CustomScrollbar/CustomScrollbar';

function Index() {
  const initialData = useMemo(() => getInitialData(CACHE_KEY), []);
  const [state, dispatch] = useReducer(reducer, {
    data: [],
    search: '',
    totalCount: 0,
    ...initialData
  });

  const { page, sort, filters, columns, data, totalCount, search, loading } = state;

  // Publish live grid state to the AI chat widget (page context overlay).
  // Without this the assistant falls back to the server-rendered list_context,
  // which is built at HTML-render time — before this grid has fetched anything
  // — so it carries no filters and an unfiltered count.
  usePublishListContext({ page, sort, filters, totalCount, data, search });

  const debouncedFilters = useDebounce(filters);

  useEffect(() => {
    saveCacheToLocalStorage(CACHE_KEY, { page, sort, filters, columns });
  }, [page, sort, filters, columns]);

  useEffect(() => {
    if (filters !== debouncedFilters) return undefined;

    dispatch({ type: actionTypes.LOADING_CHANGED, loading: true });
    const controller = new AbortController();

    api
      .get(datatableAccountsPath(), {
        params: kendoDatatableParams({ page, sort, filters: debouncedFilters, search }),
        signal: controller.signal
      })
      .then(({ data: accountsData }) => dispatch({ type: actionTypes.DATA_LOADED, data: accountsData }))
      .catch((err) => {
        if (axios.isCancel(err)) return;
        dispatch({ type: actionTypes.LOADING_CHANGED, loading: false });
      });

    return () => controller.abort();
  }, [page, sort, filters, debouncedFilters, search]);

  const onSearchChange = (value) => {
    dispatch({ type: actionTypes.SEARCH_CHANGED, search: value });
  };

  const filtersList = useFilters({ filters, dispatch });

  const onColumnsChange = useCallback(
    (cols) => dispatch({ type: actionTypes.COLUMNS_CHANGED, columns: cols.map((col) => col.id) }),
    []
  );

  const onResetFilters = () => {
    dispatch({ type: actionTypes.FILTER_CHANGED, filters: INITIAL_FILTERS });
  };

  const onResetCache = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(I18n.t('generic.are_you_sure'))) return;
    localStorage.removeItem(CACHE_KEY);
    window.location.reload();
  };

  const allColumnsWrapped = useMemo(() => ALL_COLUMNS.map((column) => ALL_COLUMNS_DATA[column]), []);
  const columnsWrapped = useMemo(() => columns.map((column) => ALL_COLUMNS_DATA[column]), [columns]);

  const columnContextValue = useMemo(
    () => ({
      onColumnsChange,
      columnsState: columnsWrapped,
      columns: allColumnsWrapped
    }),
    [columnsWrapped, allColumnsWrapped, onColumnsChange]
  );

  return (
    <KendoLocalizationProvider>
      <ColumnMenuContext.Provider value={columnContextValue}>
        <FilterPanel onResetFilters={onResetFilters} onResetCache={onResetCache} filters={filtersList}>
          <div className="filters__group--search">
            <SearchPanel placeholder={I18n.t('filters.search')} onChange={onSearchChange} />
          </div>
        </FilterPanel>
        {loading && (
          <div className="workspace__section">
            <div className="qmb-loading--96" data-title={I18n.t('generic.loading')}>
              <svg role="img">
                <use href="/map.svg#load-spinner" />
              </svg>
            </div>
          </div>
        )}
        {!loading && (
          <CustomScrollbar>
            <Grid
              className="qmb-grid--01"
              data={data}
              total={totalCount}
              skip={page.skip}
              take={page.take}
              onPageChange={(event) => {
                dispatch({ type: actionTypes.PAGE_CHANGED, page: event.page });
              }}
              pageable={{
                type: 'input',
                pageSizes: [25, 50, 100],
                pageSizeValue: page.take
              }}
              pager={EnterKeyPager}
              sortable
              sort={sort}
              onSortChange={(event) => {
                dispatch({ type: actionTypes.SORT_CHANGED, sort: event.sort });
              }}>
              {columnsWrapped.map((column) => (
                <Column
                  key={column.id}
                  field={column.field}
                  title={column.title}
                  columnMenu={ColumnMenu}
                  cell={column.cell}
                  sortable={column.sortable}
                  width={column.width}
                />
              ))}
            </Grid>
          </CustomScrollbar>
        )}
      </ColumnMenuContext.Provider>
    </KendoLocalizationProvider>
  );
}

export default Index;
